import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Wallet, TrendingUp, Clock, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (n) => `€${(n ?? 0).toFixed(2)}`;
const pct = (v) => `${Math.round((v ?? 0) * 1000) / 10}%`;
const dt = (s) => new Date(s).toLocaleDateString("it-IT");
const TYPES = {
  accrual: ["Accredito", "text-green-700"], spend: ["Speso", "text-neutral-700"], reversal: ["Storno", "text-red-600"],
  expiry: ["Scaduto", "text-neutral-400"], restore: ["Credito restituito", "text-green-700"],
};

function Stat({ label, value, icon: Icon, tone = "bg-neutral-50", testid, sub }) {
  return (
    <div className={`rounded-2xl p-4 ${tone}`}>
      <div className="flex items-center gap-1.5 text-xs opacity-70 mb-1"><Icon size={14} />{label}</div>
      <p data-testid={testid} className="font-display text-2xl font-extrabold">{value}</p>
      {sub && <p className="text-[11px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function CashbackPage() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/cashback/me").then((r) => setD(r.data)).catch(() => setD(null)); }, []);

  return (
    <div data-testid="cashback-page" className="p-4 md:p-0 space-y-5">
      <div className="flex items-center gap-2">
        <button data-testid="cashback-back" onClick={() => navigate("/profilo")} className="p-2 -ml-2 rounded-full hover:bg-neutral-100"><ArrowLeft size={20} /></button>
        <div><h1 className="font-display text-xl font-extrabold">Cashback</h1><p className="text-xs text-neutral-500">Credito interno spendibile su ENBISSY, guadagnato su ogni tuo acquisto pagato.</p></div>
      </div>

      <div className="rounded-2xl bg-black text-white p-4">
        <div className="flex items-center justify-between gap-4">
          <div><p className="text-xs opacity-70">Il tuo cashback</p><p data-testid="cb-level" className="font-display text-2xl font-extrabold">{d ? pct(d.pct) : "—"} <span className="text-[#E0B94B] text-base">del margine ENBISSY</span></p></div>
          <div className="text-right"><p className="text-xs opacity-70">Speso finora</p><p data-testid="cb-spent" className="font-display text-xl font-bold">{fmt(d?.spent_total)}</p></div>
        </div>
        <p data-testid="cb-formula" className="text-xs opacity-80 mt-3 flex items-center gap-1.5"><Sparkles size={13} />Un solo livello: su ogni ordine pagato ENBISSY trattiene il {d ? pct(d.margin_pct) : "10%"} e ti restituisce il {d ? pct(d.pct) : "10%"} di quella quota (≈ {d ? pct(d.effective_pct) : "1%"} dell'ordine). Es. ordine 100 € → {d ? fmt(100 * d.effective_pct) : "€1.00"} di credito.</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Stat label="Credito disponibile" value={fmt(d?.balance)} icon={Wallet} tone="bg-[#E0B94B]/15" testid="cb-balance" sub={`Copre fino al ${pct(d?.settings?.max_order_pct)} di un ordine`} />
        <Stat label="Sul prossimo acquisto" value={d ? pct(d.effective_pct) : "—"} icon={TrendingUp} testid="cb-pct" sub="dell'importo pagato, in credito" />
      </div>
      {d?.debt > 0 && <p data-testid="cb-debt" className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl p-3">Saldo negativo da recuperare: {fmt(d.debt)} (cashback stornato già speso). Verrà trattenuto dai prossimi accrediti.</p>}

      <div className="border border-neutral-200 rounded-2xl p-4">
        <h2 className="font-display font-bold mb-2 flex items-center gap-2"><Clock size={16} />Crediti attivi e scadenze</h2>
        <div className="divide-y divide-neutral-100">
          {(d?.credits || []).map((c) => (
            <div key={c.id} data-testid={`cb-credit-${c.id}`} className="flex justify-between py-2 text-sm">
              <div className="min-w-0"><p className="font-medium truncate">{c.title || "Cashback"}</p><p className="text-xs text-neutral-400">accreditato il {dt(c.created_at)}</p></div>
              <div className="text-right"><p className="font-display font-bold">{fmt(c.remaining)}</p><p className="text-xs text-amber-700">scade il {dt(c.expires_at)}</p></div>
            </div>
          ))}
          {d && d.credits.length === 0 && <p data-testid="cb-credits-empty" className="text-xs text-neutral-400 py-2">Nessun credito attivo: completa un acquisto per iniziare.</p>}
        </div>
      </div>

      <div className="border border-neutral-200 rounded-2xl p-4">
        <h2 className="font-display font-bold mb-2">Storico movimenti</h2>
        <div className="divide-y divide-neutral-100">
          {(d?.history || []).map((h) => (
            <div key={h.id} data-testid={`cb-mov-${h.id}`} className="flex justify-between py-2 text-sm">
              <div className="min-w-0"><p className="font-medium truncate">{h.title || TYPES[h.type]?.[0]}</p><p className="text-xs text-neutral-400">{dt(h.created_at)}{h.margin != null ? ` · margine ${fmt(h.margin)} · ${pct(h.pct)}` : ""}{h.status === "reserved" ? " · in attesa di pagamento" : ""}</p></div>
              <div className="text-right"><p className={`font-display font-bold ${h.amount < 0 ? "text-neutral-700" : "text-green-700"}`}>{h.amount > 0 ? "+" : ""}{fmt(h.amount)}</p><p className={`text-xs ${TYPES[h.type]?.[1]}`}>{TYPES[h.type]?.[0] || h.type}</p></div>
            </div>
          ))}
          {d && d.history.length === 0 && <p className="text-xs text-neutral-400 py-2">Nessun movimento ancora.</p>}
        </div>
      </div>
      <p className="text-xs text-neutral-400 text-center"><a data-testid="cb-rules-link" href="/come-funziona-cashback" className="underline">Come funziona il Cashback</a></p>
    </div>
  );
}
